import Component from '@glimmer/component';
import { service } from '@ember/service';
import { isDestroying } from '@ember/destroyable';
import { tracked } from '@glimmer/tracking';
import type StaticAssetsService from '../../../services/static-assets.ts';
import TioLoadingSpinner from '../loading-spinner.gts';
import { t } from 'ember-intl';
import Modal from 'tio-ui/components/modal';
import { dropTask } from 'ember-concurrency';
import { modifier } from 'ember-modifier';
import Button from 'tio-ui/components/button';

interface DocumentAssetSignature {
  Args: {
    assetName: string;
    content?: string;
    onCancel: () => void;
    show: boolean;
    title: string;
    updateDate?: string;
  };
}

interface DigitalAssetDataInterface {
  title: string;
  text: string;
  updateDate: string;
}

export default class DocumentAssetComponent extends Component<DocumentAssetSignature> {
  @service declare staticAssets: StaticAssetsService;

  @tracked data: DigitalAssetDataInterface | null = null;

  get title() {
    return this.data?.title ?? this.args.title;
  }

  get content() {
    return this.data?.text ?? this.args.content;
  }

  get updateDate() {
    return this.data?.updateDate ?? this.args.updateDate;
  }

  get showLoadError() {
    return Boolean(this.loadDocument.lastSuccessful) && !this.content;
  }

  // One attempt per open. Rerunning on the @show argument covers reopens within the modal's 300ms close animation, where this element survives; the microtask hop keeps the perform outside the modifier's autotracking frame — a synchronous perform entangles with task state and refetches an unreachable asset in a hot loop.
  loadOnOpen = modifier((_element: HTMLElement, [show]: [boolean]) => {
    if (!show) {
      return;
    }

    void Promise.resolve().then(() => {
      if (!isDestroying(this)) {
        void this.loadDocument.perform();
      }
    });
  });

  loadDocument = dropTask(async () => {
    if (this.args.content || this.data) {
      return;
    }

    this.data = await this.staticAssets.getStaticJson(this.args.assetName);
  });

  <template>
    <Modal
      @isOpen={{@show}}
      @onClose={{@onCancel}}
      @size="xl"
      @ariaLabel={{t "common.aria_labels.document_asset"}}
    >
      <:header>
        {{@title}}
        {{#if this.updateDate}}
          -
          {{t "common.dialog.document_asset.last_updated" date=this.updateDate}}
        {{/if}}
      </:header>
      <:body>
        {{#if this.loadDocument.isRunning}}
          <TioLoadingSpinner class="self-center" />
        {{else if this.showLoadError}}
          <p data-test-document-asset-load-error>
            {{t "common.dialog.document_asset.load_error"}}
          </p>
        {{/if}}

        <div class="raw-html" {{this.loadOnOpen @show}}>
          {{! template-lint-disable no-triple-curlies }}
          {{{this.content}}}
        </div>
      </:body>
      <:footer>
        <Button {{on "click" @onCancel}} @intent="primary">
          {{t "common.ok"}}
        </Button>
      </:footer>
    </Modal>
  </template>
}
